import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ChevronDownIcon from 'images/v5/icons/chevron-down.svg';
import ChevronUpIcon from 'images/v5/icons/chevron-up.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import ColorDot from 'layout/atoms/ColorDot';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import { Cell, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { fetchStatuses, moveStatus, removeStatus, selectStatuses, selectStatusesErrors, selectStatusesPageInfo, selectStatusesRefetchFlags, selectStatusesStatus, } from 'store/parameters/statusesSlice';
import { getStatusColorTranslationKey, getStatusColorVar } from 'utils/statusColors';
import ParametersTable from '../shared/ParametersTable';
import styles from './Statuses.module.scss';
function Statuses() {
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const statuses = useSelector(selectStatuses);
    const pageInfo = useSelector(selectStatusesPageInfo);
    const status = useSelector(selectStatusesStatus);
    const errors = useSelector(selectStatusesErrors);
    const refetchFlags = useSelector(selectStatusesRefetchFlags);
    const isMoving = status.move === 'loading';
    const fetchMore = () => {
        if (pageInfo.hasNextPage && status.fetch !== 'loading') {
            dispatch(fetchStatuses());
        }
    };
    useEffect(() => {
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchStatuses());
        }
    }, [dispatch, status.fetch, refetchFlags.fetch]);
    const handleDelete = (statusId) => {
        dispatch(removeStatus(statusId));
    };
    const handleMove = (statusId, direction) => {
        dispatch(moveStatus({ statusId, direction }));
    };
    if (status.fetch === 'loading' || status.fetch === 'idle') {
        return React.createElement(Loader, null);
    }
    if (status.fetch === 'failed') {
        return React.createElement("div", null, errors.fetch);
    }
    return (React.createElement(Grid, { gap: 24 },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } },
            React.createElement(Button, { color: "primary", onClick: () => dispatch(openModal({ name: 'statusCreator' })) }, t('add_status'))),
        statuses.length === 0 ? (React.createElement(EmptyState, { subheader: t('no_statuses') })) : (React.createElement(ParametersTable, { columns: [
                ['120px', '120px'],
                ['240px', '1fr'],
                ['240px', '1fr'],
                ['160px', '200px'],
                ['72px', 'auto'],
            ], headers: [
                { content: t('display_order') },
                { content: t('status_name_pl') },
                { content: t('status_name_en') },
                { content: t('status_color') },
                { content: null },
            ], dataLength: statuses.length, hasMore: pageInfo.hasNextPage, fetchMore: fetchMore }, statuses.map((item, index) => (React.createElement(Row, { key: item.id },
            React.createElement(Cell, null,
                React.createElement("div", { className: styles.orderActions },
                    React.createElement("span", { className: styles.orderNumber }, index + 1),
                    React.createElement(Button, { size: "xs", shape: "squared", theme: "ghost", title: t('move_status_up'), disabled: index === 0 || isMoving, leftIcon: React.createElement(ChevronUpIcon, null), onClick: () => handleMove(item.id, 'UP') }),
                    React.createElement(Button, { size: "xs", shape: "squared", theme: "ghost", title: t('move_status_down'), disabled: index === statuses.length - 1 || isMoving, leftIcon: React.createElement(ChevronDownIcon, null), onClick: () => handleMove(item.id, 'DOWN') }))),
            React.createElement(Cell, null, item.namePL),
            React.createElement(Cell, null, item.nameEN),
            React.createElement(Cell, null,
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                    React.createElement(ColorDot, { color: getStatusColorVar(item.color) }),
                    t(getStatusColorTranslationKey(item.color)))),
            React.createElement(Cell, null,
                React.createElement(Dropdown, null,
                    (item === null || item === void 0 ? void 0 : item.canBeUpdated) && (React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: () => dispatch(openModal({ name: 'statusEditor', id: item.id })) }, t('common:edit'))),
                    (item === null || item === void 0 ? void 0 : item.canBeRemoved) && (React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleDelete(item.id) }, t('common:delete'))))))))))));
}
export default memo(Statuses);
